import { calculateSimilarityScore } from "./similarity.ts";
import { comparableDisplayIdentity } from "../domain/listingIdentity.ts";
import {
  canonicalCondition,
  canonicalPropertyType,
  effectiveApartmentArea,
  municipalityName
} from "../domain/propertyMatching.ts";
import { normalizeRoomCount } from "../data/roomNormalization.ts";
import type {
  MarketFunnelRejectionReason,
  MarketFunnelRejectionSample,
  MarketFunnelSnapshot,
  MarketPriceDistribution,
  MarketSubjectSnapshot
} from "../domain/types.ts";
import type {
  MarketCandidate,
  MarketCandidateRejection
} from "../data/marketDataProvider.ts";

export const MARKET_FUNNEL_AREA_SIMILARITY_MINIMUM = 0.85;

const percentile = (sortedValues: number[], fraction: number) => {
  if (sortedValues.length === 0) return 0;
  const index = (sortedValues.length - 1) * fraction;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sortedValues[lower];
  return sortedValues[lower] + (sortedValues[upper] - sortedValues[lower]) * (index - lower);
};

const roundCurrency = (value: number) => Math.round(value);

export const calculatePriceDistribution = (
  candidates: Array<Pick<MarketCandidate, "askingPrice">>
): MarketPriceDistribution | undefined => {
  const prices = candidates
    .map((candidate) => candidate.askingPrice)
    .filter((price): price is number => typeof price === "number" && Number.isFinite(price) && price > 0)
    .sort((first, second) => first - second);
  if (prices.length === 0) return undefined;
  return {
    count: prices.length,
    min: roundCurrency(prices[0]),
    q1: roundCurrency(percentile(prices, 0.25)),
    median: roundCurrency(percentile(prices, 0.5)),
    q3: roundCurrency(percentile(prices, 0.75)),
    max: roundCurrency(prices[prices.length - 1])
  };
};

export const calculateSubjectPricePercentile = (
  candidates: Array<Pick<MarketCandidate, "askingPrice">>,
  subjectPrice?: number
) => {
  if (!subjectPrice) return undefined;
  const prices = candidates
    .map((candidate) => candidate.askingPrice)
    .filter((price): price is number => typeof price === "number" && Number.isFinite(price) && price > 0);
  if (prices.length < 3) return undefined;
  const below = prices.filter((price) => price < subjectPrice).length;
  const equal = prices.filter((price) => price === subjectPrice).length;
  return Math.round(((below + equal * 0.5) / prices.length) * 100);
};

export const subjectPositionText = (percentileValue?: number) => {
  if (percentileValue === undefined) return undefined;
  if (percentileValue < 34) return "Naša cena je v spodnej tretine tohto trhu.";
  if (percentileValue < 67) return "Naša cena je v strednej tretine tohto trhu.";
  return "Naša cena je v hornej tretine tohto trhu.";
};

const stage = (candidates: MarketCandidate[], subjectPrice?: number) => {
  const subjectPricePercentile = calculateSubjectPricePercentile(candidates, subjectPrice);
  return {
    count: candidates.length,
    priceDistribution: calculatePriceDistribution(candidates),
    subjectPricePercentile,
    subjectPositionText: subjectPositionText(subjectPricePercentile)
  };
};

const rejectionSample = (
  candidate: MarketCandidate,
  reason: MarketFunnelRejectionReason
): MarketFunnelRejectionSample => ({
  id: candidate.providerPropertyId ?? candidate.sourceUrl,
  title: comparableDisplayIdentity(candidate).title,
  area: effectiveApartmentArea(candidate).value,
  askingPrice: candidate.askingPrice,
  reason
});

const rejectionReason = (
  subject: MarketSubjectSnapshot,
  candidate: MarketCandidate,
  sourceCutoffAt: string,
  pipelineRejection?: MarketCandidateRejection
): MarketFunnelRejectionReason => {
  if (pipelineRejection?.category === "duplicate" || pipelineRejection?.reasons.includes("duplicita")) return "duplicate";
  if (pipelineRejection?.category === "invalid" || pipelineRejection?.reasons.some((reason) =>
    reason === "ponuka nie je aktívna" || reason.includes("neplatn") || reason.includes("chýba"))) return "inactive_or_invalid";

  const breakdown = calculateSimilarityScore(subject, candidate, sourceCutoffAt);
  if (breakdown.components.area.available
    && (breakdown.components.area.score ?? 0) < MARKET_FUNNEL_AREA_SIMILARITY_MINIMUM) return "area_difference";

  const subjectCondition = canonicalCondition(subject.technical?.condition ?? subject.condition);
  const candidateCondition = canonicalCondition(candidate.technical?.condition ?? candidate.condition);
  if (subjectCondition !== "unknown" && candidateCondition !== "unknown" && subjectCondition !== candidateCondition) {
    return "different_condition";
  }
  if ((breakdown.components.location.score ?? 0) <= 0.2) return "insufficient_locality";
  if (pipelineRejection?.reasons.includes("nedostatočná dátová dôvera")) return "low_confidence";
  if (pipelineRejection?.reasons.includes("nízka potvrdená podobnosť")) return "low_similarity";
  if (pipelineRejection?.reasons.includes("selection score pod prahom kvality")) return "low_selection_score";
  return "other";
};

const marketLabel = (subject: MarketSubjectSnapshot) => {
  const rooms = normalizeRoomCount(subject.rooms);
  const type = canonicalPropertyType(subject.propertyType);
  const propertyLabel = type === "apartment" && rooms
    ? `${rooms}-izbové byty`
    : subject.propertyType.toLocaleLowerCase("sk");
  return `${municipalityName(subject)} · ${propertyLabel}`;
};

export type MarketFunnelInput = {
  providerId: string;
  subject: MarketSubjectSnapshot;
  sourceCutoffAt: string;
  hardEligibleCandidates: MarketCandidate[];
  validMarketCandidates: MarketCandidate[];
  selectedPrimary: MarketCandidate[];
  marketCompetitors: MarketCandidate[];
  sameConditionMarket?: MarketCandidate[];
  otherConditionAlternatives?: MarketCandidate[];
  rejectedCandidates: MarketCandidateRejection[];
  upstreamDuplicateCount?: number;
};

export const buildMarketFunnelSnapshot = ({
  providerId,
  subject,
  sourceCutoffAt,
  hardEligibleCandidates,
  validMarketCandidates,
  selectedPrimary,
  marketCompetitors,
  sameConditionMarket = [],
  otherConditionAlternatives = [],
  rejectedCandidates,
  upstreamDuplicateCount = 0
}: MarketFunnelInput): MarketFunnelSnapshot => {
  const subjectArea = effectiveApartmentArea(subject);
  const withScores = validMarketCandidates.map((candidate) => ({
    candidate,
    breakdown: calculateSimilarityScore(subject, candidate, sourceCutoffAt)
  }));
  const similarArea = withScores
    .filter(({ breakdown }) => breakdown.components.area.available
      && (breakdown.components.area.score ?? 0) >= MARKET_FUNNEL_AREA_SIMILARITY_MINIMUM)
    .map(({ candidate }) => candidate);
  const canonicalSubjectCondition = canonicalCondition(subject.technical?.condition ?? subject.condition);
  const conditionStageAvailable = canonicalSubjectCondition !== "unknown";
  const similarCondition = conditionStageAvailable
    ? similarArea.filter((candidate) =>
      canonicalCondition(candidate.technical?.condition ?? candidate.condition) === canonicalSubjectCondition)
    : [];
  const selectedIds = new Set(selectedPrimary.map((candidate) => candidate.providerPropertyId ?? candidate.sourceUrl));
  const rejectedById = new Map(rejectedCandidates.map((rejection) => [
    rejection.candidate.providerPropertyId ?? rejection.candidate.sourceUrl,
    rejection
  ]));
  const rejectionReasonCounts = {
    area_difference: 0,
    different_condition: 0,
    insufficient_locality: 0,
    low_confidence: 0,
    low_similarity: 0,
    low_selection_score: 0,
    inactive_or_invalid: 0,
    duplicate: upstreamDuplicateCount,
    other: 0
  } satisfies Record<MarketFunnelRejectionReason, number>;
  const rejectionSamples: MarketFunnelSnapshot["rejectionSamples"] = {};

  hardEligibleCandidates.forEach((candidate) => {
    const id = candidate.providerPropertyId ?? candidate.sourceUrl;
    if (selectedIds.has(id)) return;
    const reason = rejectionReason(subject, candidate, sourceCutoffAt, rejectedById.get(id));
    rejectionReasonCounts[reason] += 1;
    if ((rejectionSamples[reason]?.length ?? 0) < 3) {
      rejectionSamples[reason] = [...(rejectionSamples[reason] ?? []), rejectionSample(candidate, reason)];
    }
  });

  const cheaperSimilarAreaRejected = subject.askingPriceAtAnalysis
    ? similarArea
      .filter((candidate) => !selectedIds.has(candidate.providerPropertyId ?? candidate.sourceUrl))
      .filter((candidate) => Boolean(candidate.askingPrice && candidate.askingPrice < subject.askingPriceAtAnalysis!))
      .map((candidate) => rejectionSample(
        candidate,
        rejectionReason(subject, candidate, sourceCutoffAt, rejectedById.get(candidate.providerPropertyId ?? candidate.sourceUrl))
      ))
      .slice(0, 5)
    : [];

  const relativeDifference = 0.6 * (1 - MARKET_FUNNEL_AREA_SIMILARITY_MINIMUM);
  const areaBand = subjectArea.value ? {
    min: Math.round(subjectArea.value * (1 - relativeDifference) * 10) / 10,
    max: Math.round(subjectArea.value / (1 - relativeDifference) * 10) / 10
  } : undefined;

  return {
    capturedAt: sourceCutoffAt,
    providerId,
    marketLabel: marketLabel(subject),
    subjectArea: subjectArea.value,
    subjectAreaBasis: subjectArea.basis,
    subjectAskingPrice: subject.askingPriceAtAnalysis,
    areaSimilarityMinimum: MARKET_FUNNEL_AREA_SIMILARITY_MINIMUM,
    areaBand,
    hardEligibleCount: hardEligibleCandidates.length,
    stages: {
      allMarket: stage(validMarketCandidates, subject.askingPriceAtAnalysis),
      similarArea: stage(similarArea, subject.askingPriceAtAnalysis),
      similarCondition: stage(similarCondition, subject.askingPriceAtAnalysis),
      primaryComparables: stage(selectedPrimary, subject.askingPriceAtAnalysis),
      marketCompetition: stage(marketCompetitors, subject.askingPriceAtAnalysis),
      sameConditionMarket: stage(sameConditionMarket, subject.askingPriceAtAnalysis),
      otherConditionAlternatives: stage(otherConditionAlternatives, subject.askingPriceAtAnalysis)
    },
    rejectionReasonCounts,
    rejectionSamples,
    cheaperSimilarAreaRejected,
    conditionStageAvailable
  };
};
